import { Box, BoxProps } from "@mui/material";
import { MAIN_CATEGORIES, MODELS_DATA } from "@shared/core";
import React, { JSX, useEffect, useRef } from "react";
import { deviceStyles, useDeviceStyles } from "../../hooks/useDeviceStyles";

import { useResponsiveScaler } from "../../lib/ResponsiveScaler";
import { useConfigStore } from "../../store";
import { useActiveCategory, useActiveProduct } from "../../store/selectors";
import { colors } from "../../styles/theme";
import { BrandLink } from "../BrandLink/BrandLink";
import { FlexOrder } from "../FlexOrder/FlexOrder";

export type MainCategoriesProps = {
  onModelClick?: () => void;
} & BoxProps;

const MAIN_CATEGORIES_WITH_ORDER = MAIN_CATEGORIES.map((category, index) => ({
  ...category,
  order: index,
}));

export const MainCategories: React.FC<MainCategoriesProps> = (
  props,
): JSX.Element => {
  const { onModelClick, ...rest } = props;
  const { sx, ...restWithNoSx } = rest;
  const { isMobileDevice } = useDeviceStyles();

  const categoriesRef = useRef(null);

  const activeProduct = useActiveProduct();
  const activeCategory = useActiveCategory();

  const setActiveMainCategory = useConfigStore((s) => s.setActiveMainCategory);
  const activeProductTitle =
    isMobileDevice && MODELS_DATA[activeProduct].titleMobile
      ? MODELS_DATA[activeProduct].titleMobile
      : MODELS_DATA[activeProduct].title;

  const activeIndex = MAIN_CATEGORIES_WITH_ORDER.findIndex(
    (c) => c.uid === activeCategory,
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const centerRef = useRef<HTMLDivElement | null>(null);

  const [sideWidth, setSideWidth] = React.useState<number>(0);

  useEffect(() => {
    if (!containerRef.current || !centerRef.current) return;

    const measure = () => {
      requestAnimationFrame(() => {
        const total = window.innerWidth - 2 * 38;
        const center = centerRef.current!.getBoundingClientRect().width;

        const next = Math.max((total - center) / 2, 0);
        setSideWidth(next);
      });
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(containerRef.current);
    ro.observe(centerRef.current);

    return () => ro.disconnect();
  }, []);

  // const { calculatedWidth } = useEqualItemWidths({
  //   selector: ".item",
  //   containerRef: categoriesRef
  // } as any);

  const { rpx } = useResponsiveScaler();
  const rpxItem = rpx({
    width: "320px",
    gap: "130px",
  });
  const calculatedWidth = parseInt(rpxItem.width);

  return (
    <>
      <Box
        ref={containerRef}
        sx={(theme) => ({
          position: "relative",
          overflow: "hidden",
        })}
        {...restWithNoSx}
      >
        {/* Main Categories | START */}
        <Box
          sx={(theme) => ({
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
            width: "100%",
            // padding: "30px 0",
            borderTop: `solid ${colors.text.v1}`,
            borderBottom: `solid ${colors.text.v1}`,
            ...deviceStyles(
              {
                mobile: {
                  padding: "0 6px",
                  borderWidth: "1px",
                },
              },
              [
                {
                  borderWidth: "2px",
                  borderTop: "none",
                },
              ],
            ),
            [theme.breakpoints.down(540)]: {
              "& .MuiTypography-root": {
                fontSize: "clamp(6px, 1.75vw, 12px) !important",
              },
            },
          })}
        >
          <BrandLink
            onClick={() => onModelClick?.()}
            sx={{
              minWidth: `${isMobileDevice ? sideWidth + 48 : sideWidth}px`,
              flexShrink: 0,
              cursor: "default",
              justifySelf: "start",
              justifyContent: "flex-start",

              display: "flex",
              alignItems: "center",
              height: "100%",

              whiteSpace: "nowrap",
              textAlign: "left",
              overflow: "visible",
            }}
            typographyProps={{
              sx: {
                lineHeight: 1.1,
              },
            }}
          >
            {activeProductTitle}
          </BrandLink>

          <Box
            ref={centerRef}
            sx={{
              display: "flex",
              justifySelf: "center",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
          >
            <Box position="relative" width="100%">
              <Box
                position="absolute"
                top={0}
                left={`${(100 / MAIN_CATEGORIES_WITH_ORDER.length) * activeIndex}%`}
                width={`${100 / MAIN_CATEGORIES_WITH_ORDER.length}%`}
                height="2px"
                bgcolor="#fff"
                sx={{ transition: "left 0.25s ease" }}
              />
              <FlexOrder
                ref={categoriesRef}
                shift={false}
                itemClassName={"item"}
                sx={{
                  ".item": {
                    ...rpx({
                      width: "580px",
                    }),
                  },
                }}
              >
                {MAIN_CATEGORIES_WITH_ORDER.map((el, i) => {
                  const { uid, name } = el;
                  // console.log('uid', uid, activeCategory);
                  return (
                    <BrandLink
                      key={uid}
                      onClick={() => setActiveMainCategory(el.uid)}
                      typographyProps={{
                        variant: "v2_special",
                        sx: {
                          color:
                            uid === activeCategory ? colors.text.v4 : undefined,
                        },
                      }}
                      sx={{
                        padding: isMobileDevice ? "6px 10px" : "16px 0px",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {name}
                    </BrandLink>
                  );
                })}
              </FlexOrder>
            </Box>
          </Box>

          <BrandLink
            $disableHover={true}
            sx={{
              minWidth: `${isMobileDevice ? sideWidth + 48 : sideWidth}px`,
              flexShrink: 0,
              cursor: "default",
              justifySelf: "end",
              justifyContent: "flex-end",
              display: "flex",
              alignItems: "center",
              height: "100%",

              whiteSpace: "nowrap",
              textAlign: "left",
              overflow: "visible",
            }}
            typographyProps={{
              sx: {
                lineHeight: 1.1,
              },
            }}
          >
            Summary
          </BrandLink>
        </Box>
      </Box>
    </>
  );
};
